import Vue from 'vue'
import Router from 'vue-router'

import menuModule from 'vuex-store/modules/menu'
import lazyLoading from '../store/modules/menu/lazyLoading'

Vue.use(Router)

const demoRoutes = []
if (process.env.NODE_ENV === 'development') {
  const VueComponentTree = require('vue-component-tree').default

  demoRoutes.push(
    VueComponentTree(require.context('./..', true, /.demo.vue$/), '/demo')
  )
}

export default new Router({
  routes: [
    ...demoRoutes,
    ...generateRoutesFromMenu(menuModule.state.items),

    // General Pages
    {
      path: '*',
      redirect: { name: 'Login' }
    },
    {
      name: 'Home',
      path: '/',
      redirect: { name: 'CommonHome' }
    },
    {
      name: 'Login',
      path: '/auth/login',
      component: lazyLoading('auth/login/Login')
    },
    {
      name: 'TokenLogin',
      path: '/auth/login/:token',
      component: lazyLoading('auth/login/TokenLogin')
    },
    {
      name: 'Logout',
      path: '/auth/logout',
      beforeEnter (to, from, next) {
        // Remove authentication
        localStorage.removeItem('fonestorm-token')
        localStorage.removeItem('fonestorm-username')
        localStorage.removeItem('flow-token')
        localStorage.removeItem('flow-login-token')
        // Remove branding
        localStorage.removeItem('branding-name')
        localStorage.removeItem('branding-logo')
        localStorage.removeItem('branding-color')
        next()
      },
      component: lazyLoading('auth/login/Login')
    },
    {
      name: 'ForgotPassword',
      path: '/auth/forgot_password',
      component: lazyLoading('auth/password/ForgotPassword')
    },
    {
      name: 'PasswordReset',
      path: '/auth/password_resets/:token',
      component: lazyLoading('auth/password/PasswordReset')
    },
    {
      name: 'CommonHome',
      path: '/home',
      component: lazyLoading('home/Home'),
      meta: {
        requiresAuth: true
      }
    },
    // User Dashboard
    {
      name: 'UserHome',
      path: '/me',
      component: lazyLoading('me/home/Home'),
      meta: {
        requiresAuth: true
      }
    },
    {
      name: 'UserDevice',
      path: '/me/devices/:id',
      component: lazyLoading('me/devices/Device'),
      meta: {
        parent: 'UserHome',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'UserCDR',
      path: '/me/cdr',
      component: lazyLoading('me/cdr/CDRs'),
      meta: {
        parent: 'UserHome',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'UserRecording',
      path: '/me/recording',
      component: lazyLoading('me/recording/Recordings'),
      meta: {
        parent: 'UserHome',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'UserStats',
      path: '/me/stats',
      component: lazyLoading('me/stats/Stats'),
      meta: {
        parent: 'UserHome',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'UserVoicemails',
      path: '/me/voicemails/:id/messages',
      component: lazyLoading('me/voicemails/VoicemailMessages'),
      meta: {
        parent: 'UserHome',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    // User Profile
    {
      name: 'Profile',
      path: '/profile',
      component: lazyLoading('controllers/Profile'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'Profile',
        access: 'read'
      }
    },
    // User Account
    {
      name: 'Account',
      path: '/account',
      component: lazyLoading('controllers/Account'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'Account',
        access: 'read'
      }
    },
    // User Account Settings
    {
      name: 'AccountSettings',
      path: '/accountsettings',
      component: lazyLoading('controllers/AccountSettings'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'Account',
        access: 'read'
      }
    },
    // Billing Page
    {
      name: 'Billing',
      path: '/billing',
      component: lazyLoading('controllers/Billing'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'Billing',
        access: 'read'
      }
    },
    {
      name: 'Rates',
      path: '/rates',
      component: lazyLoading('rates/Rates'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'Billing',
        access: 'read'
      }
    },
    // User Manager
    {
      name: 'Users',
      path: '/users',
      component: lazyLoading('controllers/Users'),
      meta: {
        parent: 'Home',
        requiresAuth: true,
        policy: 'User',
        access: 'read'
      }
    },
    {
      name: 'User',
      path: '/users/:id',
      component: lazyLoading('controllers/User'),
      meta: {
        parent: 'Users',
        requiresAuth: true,
        otherLayout: true,
        policy: 'User',
        access: 'edit'
      }
    },
    // User Manager
    {
      name: 'SubAccounts',
      path: '/subaccounts',
      component: lazyLoading('subaccounts/SubAccounts'),
      meta: {
        requiresAuth: true,
        policy: 'SubAccount',
        access: 'read'
      }
    },
    {
      name: 'SubAccount',
      path: '/subaccounts/:id',
      component: lazyLoading('subaccounts/SubAccount'),
      meta: {
        parent: 'SubAccounts',
        requiresAuth: true,
        policy: 'SubAccount',
        access: 'edit'
      }
    },
    // Business Objects
    {
      name: 'Contact',
      path: '/contacts/:id',
      component: lazyLoading('contacts/Contact'),
      meta: {
        parent: 'Contacts',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'ReadContact',
      path: '/contacts/:id/read',
      component: lazyLoading('contacts/ReadContact'),
      meta: {
        parent: 'Contacts',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'read'
      }
    },
    {
      name: 'Device',
      path: '/devices/:id',
      component: lazyLoading('devices/Device'),
      meta: {
        parent: 'Devices',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'Location',
      path: '/locations/:id',
      component: lazyLoading('locations/Location'),
      meta: {
        parent: 'Locations',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    // {
    //   name: 'E911Locations',
    //   path: '/e911locations',
    //   component: lazyLoading('e911locations/E911Locations'),
    //   meta: {
    //     requiresAuth: true,
    //     policy: 'BusinessObject',
    //     access: 'read'
    //   }
    // },
    // {
    //   name: 'E911Location',
    //   path: '/e911locations/:id',
    //   component: lazyLoading('e911locations/E911Location'),
    //   meta: {
    //     parent: 'E911Locations',
    //     requiresAuth: true,
    //     policy: 'BusinessObject',
    //     access: 'edit'
    //   }
    // },
    {
      name: 'Tree',
      path: '/fonetree',
      component: lazyLoading('tree/TreeRoot'),
      meta: {
        parent: 'Home',
        requiresAuth: true,
        policy: 'Fonenumber',
        access: 'read'
      }
    },
    {
      name: 'AddFonenumbers',
      path: '/fonenumbers/add',
      component: lazyLoading('fonenumbers/AddFonenumbers'),
      meta: {
        parent: 'Fonenumbers',
        requiresAuth: true,
        policy: 'Fonenumber',
        access: 'buy'
      }
    },
    {
      name: 'Fonenumber',
      path: '/fonenumbers/:id',
      component: lazyLoading('fonenumbers/Fonenumber'),
      meta: {
        parent: 'Fonenumbers',
        requiresAuth: true,
        policy: 'Fonenumber',
        access: 'edit'
      }
    },
    {
      name: 'Form',
      path: '/forms/:id',
      component: lazyLoading('forms/Form'),
      meta: {
        parent: 'Forms',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'RenderForm',
      path: '/forms/:id/render',
      component: lazyLoading('forms/RenderForm'),
      meta: {
        parent: 'Forms',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'PublicForm',
      path: '/forms/:id/view',
      component: lazyLoading('forms/PublicForm'),
      meta: {
        public: true
      }
    },
    {
      name: 'Document',
      path: '/documents/:id',
      component: lazyLoading('documents/Document'),
      meta: {
        parent: 'Documents',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'SoundFile',
      path: '/soundfiles/:id',
      component: lazyLoading('soundfiles/SoundFile'),
      meta: {
        parent: 'SoundFiles',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'SpeedDial',
      path: '/speeddials/:id',
      component: lazyLoading('speeddials/SpeedDial'),
      meta: {
        parent: 'SpeedDials',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'PageGroup',
      path: '/pagegroups/:id',
      component: lazyLoading('pagegroups/PageGroup'),
      meta: {
        parent: 'PageGroups',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'RingGroup',
      path: '/ringgroups/:id',
      component: lazyLoading('ringgroups/RingGroup'),
      meta: {
        parent: 'RingGroups',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'IVRMenu',
      path: '/ivrmenus/:id',
      component: lazyLoading('ivrmenus/IVRMenu'),
      meta: {
        parent: 'IVRMenus',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'Scheduler',
      path: '/schedulers/:id',
      component: lazyLoading('schedulers/Scheduler'),
      meta: {
        parent: 'Schedulers',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'Announcement',
      path: '/announcements/:id',
      component: lazyLoading('announcements/Announcement'),
      meta: {
        parent: 'Announcements',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'Conference',
      path: '/conferences/:id',
      component: lazyLoading('conferences/Conference'),
      meta: {
        parent: 'Conferences',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'DigitQuery',
      path: '/digitqueries/:id',
      component: lazyLoading('digitqueries/DigitQuery'),
      meta: {
        parent: 'DigitQueries',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'Voicemail',
      path: '/voicemails/:id',
      component: lazyLoading('voicemails/Voicemail'),
      meta: {
        parent: 'Voicemails',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'VoicemailMessages',
      path: '/voicemails/:id/messages',
      component: lazyLoading('voicemails/VoicemailMessages'),
      meta: {
        parent: 'Voicemails',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'CallerIDBlacklist',
      path: '/callerid-blacklist/:id',
      component: lazyLoading('callerid-blacklist/CallerIDBlacklist'),
      meta: {
        parent: 'CallerIDBlacklists',
        requiresAuth: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'Integrations',
      path: '/integrations',
      component: lazyLoading('integrations/Integrations'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'BusinessObject',
        access: 'read'
      }
    },
    {
      name: 'Integration',
      path: '/integrations/:id',
      component: lazyLoading('integrations/Integration'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'ZohoSettings',
      path: '/integrations/settings/zoho',
      component: lazyLoading('integrations/settings/Zoho'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'GoogleSettings',
      path: '/integrations/settings/google',
      component: lazyLoading('integrations/settings/Google'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'MS365Settings',
      path: '/integrations/settings/ms365',
      component: lazyLoading('integrations/settings/MS365'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    {
      name: 'DropboxSettings',
      path: '/integrations/settings/dropbox',
      component: lazyLoading('integrations/settings/Dropbox'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'BusinessObject',
        access: 'edit'
      }
    },
    // CDR Page
    {
      name: 'CDR',
      path: '/cdr',
      component: lazyLoading('cdr/CDRs'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'Analytics',
        access: 'read'
      }
    },
    {
      name: 'Recording',
      path: '/recording',
      component: lazyLoading('recording/Recordings'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'Analytics',
        access: 'read'
      }
    },
    {
      name: 'Stats',
      path: '/stats',
      component: lazyLoading('stats/Stats'),
      meta: {
        requiresAuth: true,
        otherLayout: true,
        policy: 'Analytics',
        access: 'read'
      }
    },
    {
      name: 'MessagingBrands',
      path: '/messaging/brands',
      component: lazyLoading('messaging-brands/Brands'),
      meta: {
        requiresAuth: true,
        otherLayout: false,
        policy: 'MessagingBrand',
        access: 'read'
      }
    },
    {
      name: 'MessagingBrand',
      path: '/messaging/brands/:id',
      component: lazyLoading('messaging-brands/Brand'),
      meta: {
        requiresAuth: true,
        otherLayout: false,
        policy: 'MessagingBrand',
        access: 'read'
      }
    },
    {
      name: 'MessagingCampaign',
      path: '/messaging/brands/:id/campaigns/:campaign_id',
      component: lazyLoading('messaging-brands/messaging-campaigns/Campaign'),
      meta: {
        requiresAuth: true,
        otherLayout: false,
        policy: 'MessagingBrand',
        access: 'read'
      }
    },
    // ScreenShare Pages
    {
      name: 'ScreenView',
      path: '/screenshare/view',
      component: lazyLoading('screenshare/View')
    },
    {
      name: 'CheckScreenView',
      path: '/screenshare',
      component: lazyLoading('screenshare/Check')
    },
    // PortOrder Manager
    {
      name: 'PortOrders',
      path: '/portorders',
      component: lazyLoading('portorders/List'),
      meta: {
        requiresAuth: true,
        policy: 'Fonenumber',
        access: 'buy'
      }
    },
    // Access Denied
    {
      name: 'AccessDenied',
      path: '/403',
      component: lazyLoading('extra/AccessDenied')
    },
    // Integrations
    {
      name: 'Intermediate',
      path: '/intermediate',
      component: lazyLoading('integrations/Intermediate'),
      meta: {
        requiresAuth: true
      }
    }
  ]
})

function generateRoutesFromMenu (menu = [], routes = []) {
  for (let i = 0, l = menu.length; i < l; i++) {
    let item = menu[i]
    if (item.path) {
      routes.push(item)
    }
    if (item.children) {
      generateRoutesFromMenu(item.children, routes)
    }
  }
  return routes
}



// WEBPACK FOOTER //
// ./src/router/index.js